import Request from './request';
import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import { v4 as uuidv4 } from 'uuid';
import FieldNames from '../defaults/FieldNames.json';
import {formatDateForDisplay} from "../utils/DateHelper";
import GeneralService from './GeneralService';

const UserService = {
  getAllUsers: function(rootServiceUrl, config) {
    try
    {
      const queryObject = new Query({
          where: `(OBJECTID IS NOT NULL)`,
          outFields: "*",
          returnGeometry: false,
      });

      return query.executeQueryJSON(rootServiceUrl + "/" + config.users[config.env].layerId, queryObject).then(results => {
        let userItems = [];
        if(results.features) {
          userItems = results.features.map((eaItem) => eaItem.attributes);
        } 
        return userItems;
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getAllUsersAndRoles: function(rootServiceUrl, config) {
    try
    {
      const queryObject = new Query({
        where: `(OBJECTID IS NOT NULL)`,
        outFields: "*",
        returnGeometry: false,
    });

    return query.executeQueryJSON(rootServiceUrl + "/" + config.users[config.env].layerId, queryObject).then(results => {
      let userItems = [];
      let userIds = [];
      if(results.features) {
        userItems = results.features.map((eaItem) => eaItem.attributes);
        userIds = userItems.map((user) => user[FieldNames.userFields.id]);
        let idJoinString = userIds.join(",");
        const roleQueryObject = new Query({
          where: `(${FieldNames.userRoles.usersJoinID} IN (${idJoinString}))`,
          outFields: "*",
          returnGeometry: false,
        });

        return query.executeQueryJSON(rootServiceUrl + "/" + config.userRoles[config.env].layerId, roleQueryObject).then(results => {
          let userRoles = [];
          if(results.features) {
            userRoles = results.features.map((eaItem) => eaItem.attributes);
            
            let usersWithRoles = [];
            userItems.forEach((user) => {
              let filteredUserRoles = userRoles.filter((roles) => roles[FieldNames.userFields.userID] === user[FieldNames.userFields.userID]);
              let hasRole = filteredUserRoles && filteredUserRoles.length > 0;
              // 8-22-23: A user can only have one Role/Department/Agency but multiple Regions/Units
                // 6-26-26: users can now have multiple agencies
              let userRole = hasRole ? filteredUserRoles[0][FieldNames.userRoles.appRole] : '';
              let userAgency = hasRole
                  ? [...new Set(filteredUserRoles.map((ur) => ur[FieldNames.userRoles.agency]))].join(", ")
                  : '';
              let userRegionString = hasRole ? [...new Set(filteredUserRoles.map((ur) => ur[FieldNames.userRoles.region]))].join(", ") : '';
              
              let userObj = {
                [FieldNames.userFields.id]: user[FieldNames.userFields.id],
                [FieldNames.userFields.objectID]: user[FieldNames.userFields.objectID],
                [FieldNames.userFields.userID]: user[FieldNames.userFields.userID],
                [FieldNames.userFields.nifcUsername]: user[FieldNames.userFields.nifcUsername],
                [FieldNames.userRoles.appRole]: userRole,
                [FieldNames.userRoles.agency]: userAgency,
                [FieldNames.userRoles.region]: userRegionString,
                [FieldNames.userFields.lastLogin]: formatDateForDisplay(user[FieldNames.userFields.lastLogin])
              }

              usersWithRoles.push(userObj);
            })

            return usersWithRoles;
          } 
        });
      } 
    });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getUserByNifcUsername: function(rootServiceUrl, nifcUserName, config) {
    try
    {
      const queryObject = new Query({
        where: "((NifcUsername = '" + nifcUserName + "') AND (IsActive = 'Yes'))",
        outFields: "*",
        returnGeometry: false,
    });

    return GeneralService.queryCallDirect(rootServiceUrl + "/" + config.users[config.env].layerId, queryObject).then(results => {
      let userItems = [];
      if(results.features) {
        userItems = results.features.map((eaItem) => eaItem.attributes);
      } 
      return userItems;
    });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  getUserRoles: function(rootServiceUrl, userID, config) {
    try
    {
      const queryObject = new Query({
          where: "(UsersJoinID = " + userID + ")",
          outFields: "*",
          returnGeometry: false,
      });

      return query.executeQueryJSON(rootServiceUrl + "/" + config.userRoles[config.env].layerId, queryObject).then(results => {
        let userRoles = [];
        if(results.features) {
          userRoles = results.features.map((eaItem) => eaItem.attributes);
        } 
        return userRoles;
      });
      }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addUser: function (rootServiceUrl, nifcToken, userInfo, config) {
    try
    {
      const newUser = 
      {
          attributes: userInfo
      }
      return new Promise((resolve, reject) => {
        new Request().makeRequest({
          url: rootServiceUrl + "/" + config.users[config.env].layerId + "/addFeatures",
          method: 'post',
          data: {
              f: 'json',
              token: nifcToken,
              features: JSON.stringify([newUser])
          }
        }).then(
            resp => {
              resolve(resp)
            },
            error => {
              reject(error)
            }
        );
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addUserRoles: function (rootServiceUrl, nifcToken, userRoles, config) {
    try
    {
      let adjustedUserRoles = [];
      userRoles.forEach((role) => {
        const newRole = {
          attributes: role
        }
        adjustedUserRoles.push(newRole);
      })
      return new Promise((resolve, reject) => {
        new Request().makeRequest({
          url: rootServiceUrl + "/" + config.userRoles[config.env].layerId + "/addFeatures",
          method: 'post',
          data: {
              f: 'json',
              token: nifcToken,
              features: JSON.stringify(adjustedUserRoles)
          }
        }).then(
            resp => {
              resolve(resp)
            },
            error => {
              reject(error)
            }
        );
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  updateUser: function (rootServiceUrl, nifcToken, userInfo, config) {
    const mutableUserInfo = {...userInfo} // userInfo is simple object - spread to un-freeze redux-based value
    try
    {
      return new Promise((resolve, reject) => {
        if(mutableUserInfo && !mutableUserInfo[FieldNames.userFields.userID]){
          mutableUserInfo[FieldNames.userFields.userID] = uuidv4();
        }
        let user = {
            attributes: mutableUserInfo
        };
        new Request().makeRequest({
            url: rootServiceUrl + "/" + config.users[config.env].layerId + "/updateFeatures",
            method: 'post',
            data: {
                f: 'json',
                token: nifcToken,
                features: JSON.stringify([user])
            }
        }).then(
            resp => {
                resolve(resp)
            },
            error => {
              reject(error)
            }
        );
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  deleteUsersRoles: function (rootServiceUrl, nifcToken, userRoles, config) {
    try
    {
      //Using this to delete roles before adding new ones
      return new Promise((resolve, reject) => {
        let whereArray = [];
        userRoles.forEach((role) => {
            whereArray.push("(OBJECTID = " + role.OBJECTID + ")");
        })
        const whereClause = "(" + whereArray.join(" OR ") + ")";
          new Request().makeRequest({
              url: rootServiceUrl + "/" + config.userRoles[config.env].layerId + "/deleteFeatures",
              method: 'post',
              data: {
                  f: 'json',
                  token: nifcToken,
                  where: whereClause
              }
          }).then(
              resp => {
                  resolve(resp)
              },
              error => {
                reject(error)
              }
          );
          
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
};

export default UserService;